import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { formsActions } from '../forms-actions';
import { catchError, combineLatestWith, exhaustMap, filter, map, of, tap } from 'rxjs';
import { StoreCollectionForm } from '../forms-state';
import { CollectionFormApiService } from '../../../../api/bizzmine/collection-form/collection-form-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CollectionFormEditApiService } from '../../../../api/bizzmine/collection-form-edit/collection-form-edit-api.service';
import { AlertService } from '../../../../features/bizzmine/alerts/alert.service';
import { refreshActions } from '../../refresh/refresh-actions';
import { Store } from '@ngrx/store';
import { selectForm } from '../forms-selectors';
import { CollectionFormJsonService } from '../../../../features/bizzmine/form/services/collection-form-json.service';
import { TaskType } from '../../../../../models/ts/task-type.model';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { CollectionForm } from '../../../../../models/ts/collection-form.model';
import { ApiInterceptor } from '../../../../core/interceptors/api/api.interceptor';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import { CollectionFormValidatorError } from '../../../../features/bizzmine/form/classes/collection-form-validator-error';
import { BreakpointEnum } from '../../../../shared/enums/pixel-breakpoints';
import { SAVED_ALERT } from '../../../../features/bizzmine/alerts/constants/preset-alerts';

/**
 * Effects for forms with a task
 */
@Injectable()
export class FormsTaskEffects {
  private actions$ = inject(Actions);
  private collectionFormApiService = inject(CollectionFormApiService);
  public getAnonymousForm$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.getAnonymousForm),
    exhaustMap(({
                  collectionId,
                  formId
                }) => this.collectionFormApiService.getAnonymousForm(collectionId).pipe(combineLatestWith(of(formId)))
      .pipe(
        map(
          ([data, formId]) => {
            if (formId != null && formId.trim() != '') {
              return ({
                type: formsActions.formRefreshed.type,
                form: data,
                formId: formId
              });
            }
            return ({
              type: formsActions.formFetched.type,
              form: new StoreCollectionForm(data),
              addToViewStack: true
            });
          }
        )
      ))
  ));
  private router = inject(Router);
  public redirectToExam$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.redirectToExam),
    exhaustMap(request => this.collectionFormApiService.getExamDetails({ taskId: request.taskId })),
    tap(data => {
      this.router.navigate([`./trn/${data.TrainingAppID}/exam/${data.ExamSessionVersionsID}`])
        .then();
    })
  ), { dispatch: false });
  private translationService = inject(TranslationService);
  private collectionFormEditApiService = inject(CollectionFormEditApiService);
  private alertService = inject(AlertService);
  public displayFormlessException$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.displayFormlessException),
    tap(data => {
      if (ApiInterceptor.isValidationResult(data.error) && data.error.error.Errors != undefined) {
        const errorMessages = (data.error.error.Errors as Array<CollectionFormValidatorError>).map((result) => result.Message);
        this.alertService.setAlert({
          type: 'warning',
          content: errorMessages,
          title: '',
          icon: 'triangle-exclamation',
          dismissable: true,
          timed: false
        });
      }
    })
  ), { dispatch: false });
  private store$ = inject(Store);
  public getTaskForm$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.getTaskForm),
    exhaustMap(({
                  taskId,
                  formId
                }) => this.collectionFormApiService.getFormByTaskID(taskId).pipe(combineLatestWith(of(formId)))
      .pipe(
        map(
          ([data, formId]) => {
            if (data.TaskType == TaskType.TrainingAppExaminationTask) {
              return formsActions.redirectToExam({ taskId });
            }
            if (formId != null && formId.trim() != '') {
              return ({
                type: formsActions.formRefreshed.type,
                form: data,
                formId: formId
              });
            }
            return ({
              type: formsActions.formFetched.type,
              form: new StoreCollectionForm(data),
              addToViewStack: true
            });
          }
        ),
        catchError((err) => {
          this.store$.dispatch(formsActions.displayFormlessException({
            error: err
          }));
          throw err;
        })
      ))
  ));
  public getTaskFormOnBehalfOf$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.getTaskOnBehalfOf),
    exhaustMap((request) => this.collectionFormApiService.getFormByTaskIdOnBehalfOf(request.taskId, request.collectionsId, request.assignedToId, request.assignedToType).pipe(combineLatestWith(of(request)))
      .pipe(
        map(([data, request]) => {
            if (request.formId != null && request.formId.trim() != '') {
              return ({
                type: formsActions.formRefreshed.type,
                form: data,
                formId: request.formId
              });
            }
            return ({
              type: formsActions.formFetched.type,
              form: new StoreCollectionForm(data),
              addToViewStack: true
            });
          }
        ),
        catchError((err) => {
          this.store$.dispatch(formsActions.displayFormlessException({
            error: err
          }));
          throw err;
        })
      ))
  ));
  public saveTask$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.saveTask),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    filter(([props, form]) => form !== undefined),
    map(([props, form]) => ({ props, form: form!.data as CollectionForm })),
    exhaustMap(({ props, form }) => this.collectionFormEditApiService.saveWithJson(
        form.CollectionsID,
        form.CollectionFormId,
        form.TasksID,
        form.TaskInfo?.ActionsID ?? 0,
        form.TaskType,
        form.MethodType,
        JSON.stringify(CollectionFormJsonService.formToJson(form))
      ).pipe(
        map(data => formsActions.executeTaskSucceeded({
            formId: props.formId,
            versionId: data.VersionsID,
            instanceId: data.InstancesID,
            collectionId: data.CollectionsID,
            close: props.closeForm
          })
        ),
        catchError((response: HttpErrorResponse) => of(formsActions.saveTaskFailed({
            formId: props.formId,
            response: response
          }))
        )
      )
    )
  ));
  public executeTask$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.executeTask),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    filter(([props, form]) => form !== undefined && !form.data.IsExecuteOnBehalfOf && !props.preValidate),
    map(([props, form]) => ({ props, form: form!.data as CollectionForm })),
    exhaustMap(({ props, form }) => this.collectionFormEditApiService.saveAndCloseStepWithJson(
        form.CollectionsID,
        form.CollectionFormId,
        form.TasksID,
        form.TaskInfo?.ActionsID ?? 0,
        form.TaskType,
        form.MethodType,
        JSON.stringify(CollectionFormJsonService.formToJson(form))
      ).pipe(
        map(data => formsActions.executeTaskSucceeded({
            formId: props.formId,
            versionId: data.VersionID,
            instanceId: data.InstancesID,
            collectionId: data.CollectionsID,
            close: props.closeForm
          })
        ),
        catchError((response: HttpErrorResponse) => of(formsActions.executeTaskFailed({
            formId: props.formId,
            response: response
          }))
        )
      )
    )
  ));
  public preValidateTask$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.executeTask),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    filter(([props, form]) => form !== undefined && !form.data.IsExecuteOnBehalfOf && props.preValidate),
    map(([props, form]) => ({ props, form: form!.data as CollectionForm })),
    exhaustMap(({ props, form }) => this.collectionFormEditApiService.preValidateForm(
      form.CollectionsID,
      form.CollectionFormId,
      form.TasksID,
      form.MethodType,
      props.closeForm,
      form
    ).pipe(
      map(data => formsActions.executeTask({ formId: props.formId, closeForm: props.closeForm, preValidate: false })),
      catchError((response: HttpErrorResponse) => of(formsActions.preValidationFailed({
          formId: props.formId,
          response: response
        }))
      )
    ))
  ));
  public preValidateTaskOnBehalfOf$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.executeTask),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    filter(([props, form]) => form !== undefined && !!form.data.IsExecuteOnBehalfOf && props.preValidate),
    map(([props, form]) => ({ props, form: form!.data as CollectionForm })),
    exhaustMap(({ props, form }) => this.collectionFormEditApiService.preValidateFormOnBehalfOf(
      form.CollectionsID,
      form.CollectionFormId,
      form.TasksID,
      form.MethodType,
      form.AssignedToID,
      props.closeForm,
      form
    ).pipe(
      map(data => formsActions.executeTask({ formId: props.formId, closeForm: props.closeForm, preValidate: false })),
      catchError((response: HttpErrorResponse) => of(formsActions.preValidationFailed({
          formId: props.formId,
          response: response
        }))
      )
    ))
  ));
  public executeTaskOnBehalfOf$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.executeTask),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    filter(([props, form]) => form !== undefined && !!form.data.IsExecuteOnBehalfOf && !props.preValidate),
    map(([props, form]) => ({ props, form: form!.data as CollectionForm })),
    exhaustMap(({ props, form }) => this.collectionFormEditApiService.saveAndCloseStepOnBehalfOfWithJson(
        form.CollectionsID,
        form.CollectionFormId,
        form.TasksID,
        form.TaskInfo?.ActionsID ?? 0,
        form.TaskType,
        form.MethodType,
        form.AssignedToID,
        form.AssignedToType,
        JSON.stringify(CollectionFormJsonService.formToJson(form))
      ).pipe(
        map(data => formsActions.executeTaskSucceeded({
            formId: props.formId,
            versionId: data.VersionID,
            instanceId: data.InstancesID,
            collectionId: data.CollectionsID,
            close: props.closeForm
          })
        ),
        catchError((response: HttpErrorResponse) => of(formsActions.executeTaskFailed({
            formId: props.formId,
            response: response
          }))
        )
      )
    )
  ));
  public executeTaskSucceeded$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.executeTaskSucceeded),
    tap(() => this.alertService.setAlert(SAVED_ALERT)),
    concatLatestFrom((props) => this.store$.select(selectForm(props.formId))),
    tap(([props, form]) => {
        if (form != null) {
          this.store$.dispatch(formsActions.deleteSelfLock({ formId: form.id, form: form.data, close: false }));
          this.store$.dispatch(refreshActions.refreshData({ formId: form.id, collectionId: form.data.CollectionsID }));
        }
      }
    ),
    map(([props, form]) => {
      if (!props.close && form !== undefined) {
        return formsActions.refreshForm({
          formId: props.formId,
          collectionId: form!.data.CollectionsID,
          instanceId: props.instanceId,
          versionId: props.versionId,
          guidChecksum: props.guidChecksum
        });
      } else {
        return formsActions.formClosed({
          formId: props.formId,
          unsavedChanges: false
        });
      }
    })
  ));
}